// pgsql value-slot adapter (T8, Docs/AutocompleteValueSlot-Plan.md) — turns
// the expression arm's existing column discovery into normalized
// ValueSlotColumnDescriptors for the shared policy (D10). CTE/derived
// projection goes through the SAME machinery the arm uses
// (`resolveCteColumns` / `inferSubqueryColumns`) — not reimplemented.

import type { AliasBinding, CteBinding, FkEdge } from '@/core/engines';
import type { ValueSlotInfo } from '@/features/sql-grammar/model';
import { inferSubqueryColumns } from '../resolver/inferSubqueryColumns';
import { resolveCteColumns } from '../resolver/resolveCteColumns';
import { resolveLhsInScope, type ScopedOwner } from '../valueSlot/scopedResolve';
import {
  coarseTypeOfDataType,
  isBooleanishDataType,
  type ValueSlotColumnDescriptor,
  type ValueSlotEngineAdapter,
  type ValueSlotRoutineDescriptor,
  type ValueSlotScope,
  type ValueSlotVirtualColumn,
} from '../valueSlot/types';
import { foldIdentifier, formatIdentifier, resolvePgRelation } from './pgsqlIdentifiers';

type AdapterTable = {
  readonly name: string;
  readonly schema?: string;
  readonly columns: ReadonlyArray<{ readonly name: string; readonly dataType: string }>;
};
type AdapterRoutine = {
  readonly name: string;
  readonly schema?: string;
  readonly kind: string;
  readonly returns?: string;
};

/** Everything the adapter needs that is independent of the LHS operand. */
type PgsqlAdapterSources = {
  readonly text: string;
  readonly aliasBindings: readonly AliasBinding[];
  readonly cteBindings: readonly CteBinding[];
  readonly snapshotTables: ReadonlyArray<AdapterTable>;
  readonly snapshotRoutines: ReadonlyArray<AdapterRoutine>;
  readonly snapshotForeignKeys: readonly FkEdge[];
  readonly searchPath: readonly string[];
  /** `searchPath` is the session's own — exact (`PgSearchPath.exact`). */
  readonly searchPathExact: boolean;
  /** C2 P1-1 — resolver-supplied fallback for bindings whose relation is a
   *  VIRTUAL system catalog (information_schema/pg_catalog): the resolver
   *  passes a closure over its own static column map. Consulted only when
   *  the snapshot lookup yields nothing. */
  readonly virtualColumnsFor?: (binding: AliasBinding) => readonly ValueSlotVirtualColumn[] | undefined;
};

/** Scalar-value mode: a slot is REQUIRED. */
export type PgsqlValueSlotContext = PgsqlAdapterSources & {
  readonly slot: ValueSlotInfo;
};

/** Predicate-head mode (PH-D3, P1-sol F7): NO slot — a bare boolean-expression
 *  head has no LHS operand, so the scope resolves to
 *  `{ lhsType: 'unknown', lhsBooleanish: false }` and no column is ever
 *  flagged `fkPartnerOfLhs`. Declared as `slot?: undefined` rather than by
 *  omission so the overloads stay mutually exclusive: a caller passing a slot
 *  cannot land on this signature, and an existing scalar-value caller that
 *  DROPPED its slot fails to compile instead of silently degrading to
 *  no-LHS discovery. */
export type PgsqlPredicateHeadContext = PgsqlAdapterSources & {
  readonly slot?: undefined;
};

/** Implementation-side union — never the public parameter type. */
type PgsqlAdapterContext = PgsqlValueSlotContext | PgsqlPredicateHeadContext;

/** Search-path-aware relation lookup — the resolver's own rule
 *  (`resolvePgRelation`: explicit schema → exact; else search_path order; off
 *  the path only when the path is a guess). */
function lookupRelation(
  tables: ReadonlyArray<AdapterTable>,
  binding: AliasBinding,
  args: Pick<PgsqlAdapterSources, 'searchPath' | 'searchPathExact'>,
): AdapterTable | undefined {
  return resolvePgRelation(
    tables,
    foldIdentifier(binding.relation),
    binding.schema ? foldIdentifier(binding.schema) : null,
    { schemas: args.searchPath, exact: args.searchPathExact },
  );
}

function fkPartner(
  fks: readonly FkEdge[],
  lhs: { table: AdapterTable; column: string },
  cand: { table: AdapterTable; column: string },
): boolean {
  const match = (
    e: FkEdge,
    a: { table: AdapterTable; column: string },
    b: { table: AdapterTable; column: string },
  ) =>
    e.fromColumns.length === 1 &&
    e.fromTable.toLowerCase() === a.table.name.toLowerCase() &&
    e.fromColumns[0]!.toLowerCase() === a.column.toLowerCase() &&
    e.toTable.toLowerCase() === b.table.name.toLowerCase() &&
    e.toColumns[0]!.toLowerCase() === b.column.toLowerCase();
  return fks.some((e) => match(e, lhs, cand) || match(e, cand, lhs));
}

/** Resolve the slot's LHS to (owners, coarse type, boolean-ish) — the D5
 *  all-agree rule collapses disagreeing owners to 'unknown'. */
function resolveLhs(args: PgsqlAdapterContext): {
  owners: readonly ScopedOwner[];
  scope: ValueSlotScope;
} {
  // No slot (predicate-head mode) → no LHS → the neutral scope, via the same
  // path a slot with an unresolvable LHS already took.
  const lhs = args.slot?.lhs;
  if (!lhs) return { owners: [], scope: { lhsType: 'unknown', lhsBooleanish: false } };
  const owners = resolveLhsInScope({
    lhs,
    aliasBindings: args.aliasBindings,
    cteBindings: args.cteBindings,
    snapshotTables: args.snapshotTables.map((t) => ({
      name: t.name,
      schema: t.schema,
      kind: 'table' as const,
      columns: t.columns.map((c) => ({
        name: c.name, dataType: c.dataType, nullable: true, isPrimaryKey: false, ordinal: 0,
      })),
    })),
    source: args.text,
    engineId: 'pgsql',
    fold: foldIdentifier,
  });
  const types = owners.map((o) =>
    coarseTypeOfDataType('dataType' in o.column ? o.column.dataType : undefined),
  );
  const allAgree = types.length > 0 && types.every((t) => t === types[0]);
  const bools = owners.map((o) =>
    isBooleanishDataType('dataType' in o.column ? o.column.dataType : undefined, 'pgsql'),
  );
  return {
    owners,
    scope: {
      lhsType: allAgree ? types[0]! : 'unknown',
      lhsBooleanish: bools.length > 0 && bools.every(Boolean),
    },
  };
}

export function buildPgsqlValueSlotAdapter(args: PgsqlValueSlotContext): {
  adapter: ValueSlotEngineAdapter;
  scope: ValueSlotScope;
};
export function buildPgsqlValueSlotAdapter(args: PgsqlPredicateHeadContext): {
  adapter: ValueSlotEngineAdapter;
  scope: ValueSlotScope;
};
export function buildPgsqlValueSlotAdapter(args: PgsqlAdapterContext): {
  adapter: ValueSlotEngineAdapter;
  scope: ValueSlotScope;
} {
  const { owners, scope } = resolveLhs(args);
  const lhsOwner = owners.length === 1 ? owners[0] : undefined;
  const lhsTable =
    lhsOwner && (lhsOwner.binding.kind === 'table' || lhsOwner.binding.kind === 'view')
      ? lookupRelation(args.snapshotTables, lhsOwner.binding, args)
      : undefined;
  const lhsColumn = lhsOwner ? lhsOwner.column.name : undefined;

  const collectScopedColumns = (): readonly ValueSlotColumnDescriptor[] => {
    const out: ValueSlotColumnDescriptor[] = [];
    for (const b of args.aliasBindings) {
      const spelling = b.aliasSql ?? b.alias;
      const push = (name: string, dataType: string | undefined, table?: AdapterTable) => {
        out.push({
          label: name,
          insertBare: formatIdentifier(name),
          insertQualified: `${spelling}.${formatIdentifier(name)}`,
          owner: b,
          coarseType: coarseTypeOfDataType(dataType),
          booleanish: isBooleanishDataType(dataType, 'pgsql'),
          fkPartnerOfLhs:
            table !== undefined &&
            lhsTable !== undefined &&
            lhsColumn !== undefined &&
            fkPartner(
              args.snapshotForeignKeys,
              { table: lhsTable, column: lhsColumn },
              { table, column: name },
            ),
        });
      };
      if (b.kind === 'subquery' || b.kind === 'derived') {
        const names =
          b.syntheticColumns && b.syntheticColumns.length > 0
            ? b.syntheticColumns
            : b.bodyRange
              ? inferSubqueryColumns({
                  source: args.text,
                  bodyRange: b.bodyRange,
                  engineId: 'pgsql',
                  snapshotTables: args.snapshotTables,
                  cteBindings: args.cteBindings,
                })
              : [];
        for (const n of names) push(n, undefined);
        if (names.length === 0) {
          for (const v of args.virtualColumnsFor?.(b) ?? []) push(v.name, v.dataType);
        }
        continue;
      }
      if (b.kind === 'cte') {
        const cte = args.cteBindings.find(
          (c) => foldIdentifier(c.name) === foldIdentifier(b.relation),
        );
        if (!cte) continue;
        const names = resolveCteColumns({
          cte,
          source: args.text,
          engineId: 'pgsql',
          snapshotTables: args.snapshotTables,
          cteBindings: args.cteBindings,
        });
        for (const n of names) push(n, undefined);
        continue;
      }
      const tbl = lookupRelation(args.snapshotTables, b, args);
      if (!tbl) {
        for (const v of args.virtualColumnsFor?.(b) ?? []) push(v.name, v.dataType);
        continue;
      }
      for (const c of tbl.columns) push(c.name, c.dataType, tbl);
    }
    return out;
  };

  const collectScalarRoutines = (): readonly ValueSlotRoutineDescriptor[] =>
    collectPgsqlScalarRoutineDescriptors(args.snapshotRoutines);

  return {
    adapter: { engine: 'pgsql', collectScopedColumns, collectScalarRoutines },
    scope,
  };
}

/** Scalar-routine descriptor mapping shared by this adapter's
 *  `collectScalarRoutines` closure and the resolver's T18b typed-VALUES-
 *  element call site (`emitTypedValuesElement` in `pgsqlResolver.ts`) — kept
 *  in one place so the two can't drift. */
export function collectPgsqlScalarRoutineDescriptors(
  snapshotRoutines: ReadonlyArray<AdapterRoutine>,
): readonly ValueSlotRoutineDescriptor[] {
  return snapshotRoutines
    .filter((r) => r.kind !== 'procedure')
    .map((r) => ({
      label: r.name,
      insertText: `${r.name}($0)`,
      returnsCoarse: coarseTypeOfDataType(r.returns),
      returnsRaw: r.returns,
      // PH-D4 (P1-sol F6) — read only by the predicate-head policy, where a
      // boolean return promotes to T3. Harmless to the value-slot policy,
      // which ignores the field.
      returnsBooleanish: isBooleanishDataType(r.returns, 'pgsql'),
    }));
}
